// Native Email-Marketing types for the Loyalty module (Campaigns / Flows).
// These mirror the `stores/{storeId}/loyalty/{campaigns,flows}`
// backend contract one-to-one — the wire is camel/snake mixed exactly as typed
// here, so the gateway passes payloads through untouched (no snake↔camel
// mapper needed, unlike the members/overview surfaces).

import type {
  Block,
  EmailSettings,
  TemplateKey,
} from '../features/Loyalty/marketing/builder/blocks/types'

// ── Campaigns ────────────────────────────────────────────────────────────────

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  // Handing the send over before anything goes out; locked like 'sending'.
  | 'preparing'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'paused'

// Display-only status vocabulary (reference mock): lifecycle statuses are
// unchanged on the wire, the UI just renames sending→running and
// sent→completed for pills / filters. Flows share the vocabulary (live→running).
export type CampaignDisplayStatus =
  | 'draft'
  | 'scheduled'
  | 'preparing'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'

export const displayStatusForCampaign = (campaign: {
  status: CampaignStatus
}): CampaignDisplayStatus => {
  if (campaign.status === 'sending') return 'running'
  if (campaign.status === 'sent') return 'completed'
  return campaign.status
}

export const displayStatusForFlow = (flow: {
  status: FlowStatus
}): CampaignDisplayStatus => {
  if (flow.status === 'live') return 'running'
  return flow.status
}

// v2 campaign types (spec §1.1). 'sms' is accepted by the serializer but the
// create/patch view rejects it ("SMS campaigns are coming soon") — the builder
// shows it disabled. 'abandoned_cart' is realised as an always-on flow.
export type CampaignType = 'email' | 'abandoned_cart' | 'sms'

export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  email: 'Email',
  abandoned_cart: 'Abandoned Cart',
  sms: 'SMS',
}

// Display type for any stored campaign. The server's `display_type` wins
// when present (forward-compat — `_serialize_campaign` does not emit it
// today). Otherwise an SMS channel wins over `type`: the server normalises a
// legacy bonus/winback/… `type` to 'email' while `channel` stays 'sms', so
// `{type: 'email', channel: 'sms'}` is an SMS campaign. Then v2 `type` values
// pass through and anything else reads as 'email'.
export const normalizeCampaignType = (
  type: CampaignType | LegacyCampaignType | undefined,
  channel?: CampaignChannel,
  displayType?: CampaignType,
): CampaignType => {
  if (displayType) return displayType
  if (channel === 'sms') return 'sms'
  if (type === 'email' || type === 'abandoned_cart' || type === 'sms')
    return type
  return 'email'
}

// Pre-v2 campaign types. Still deserialised for existing rows; the UI shows
// them as 'email'. Never sent on create/update.
export type LegacyCampaignType =
  | 'bonus'
  | 'winback'
  | 'tier'
  | 'reward'
  | 'referral'

export type CampaignChannel = 'email' | 'sms'

export type CampaignAudienceType = 'all' | 'percentage' | 'ids' | 'segment'
// 'draft' saves without enqueueing anything (builder "Save Draft").
// 'smart' is Smart send time: enqueued immediately like 'now', but the server
// spreads the audience over the next 24h, each recipient landing on the hour
// they historically open email.
export type CampaignSendMode = 'now' | 'schedule' | 'smart' | 'draft'

// How a saved campaign was launched, echoed back on the document so the list
// and the builder can label it. Campaigns created before this rolled out read
// as 'now'.
export type CampaignScheduleMode = 'now' | 'schedule' | 'smart'

// Optional reward/incentive attached to a campaign or flow at create time and
// returned on the document. Constraint fields are omitted when left blank.
export interface CampaignReward {
  reward_key: string
  reward_label: string
  per_member_limit?: number
  budget_cap?: number
  min_order_value?: number
  stack_promo?: boolean
}

// The builder's "Additional Filters" (all/segment/percentage audiences; the
// server drops them for hand-picked ids). Both default when omitted.
export type CampaignPurchaseFilter =
  | 'any'
  | 'purchased'
  | 'repeat_3'
  | 'spent_200'
  | 'recent_30'
export type CampaignExcludeFilter = 'none' | 'recent_campaign_7d'

export interface CampaignAudienceFilters {
  purchase?: CampaignPurchaseFilter
  exclude?: CampaignExcludeFilter
}

export interface CampaignAudience {
  type: CampaignAudienceType
  customer_ids?: string[]
  percentage?: number
  // One or more segments — recipients are the union, de-duplicated by the
  // server. Always emitted by the server; `segment_id` (singular) is only
  // still read for docs saved before the multi-select builder.
  segment_ids?: string[]
  segment_id?: string
  filters?: CampaignAudienceFilters
}

// The fixed email layout's content. Shared verbatim by campaigns and flows
// (spec §1.1 / §1.2) — the server renders one layout from these fields, so
// there is no template to pick.
export interface CampaignEmailContent {
  subject?: string
  // Inbox preheader.
  preview_text?: string
  // Rich text from the builder; the server sanitises it to an allow-list
  // (p, br, strong, em, u, a[href], ul, ol, li, h1–h3, img, span[style colour]).
  body_html?: string
  // CTA button — omitted from the rendered email when cta_url is empty.
  cta_text?: string
  // May contain tokens (e.g. {{cart_url}}); must be http(s) after substitution.
  cta_url?: string
  // null → the store's brand logo (GET campaigns/brand-logo) at render time.
  logo_url?: string | null
  coupon_code?: string
  // Sender. When absent on create the server fills them from the store's
  // SMTP settings (smtp_from_email / smtp_from_name). NB: the read
  // serializers emit these as null when unset (typed as string here because
  // the write serializers reject null — coalesce before re-sending, see
  // CampaignsTab.duplicateRow).
  from_email?: string
  from_label?: string
  // Block editor (v2.1). 'layout' (or absent) = the legacy fixed layout built
  // from the fields above; 'blocks' = the server sends `compiled_html`
  // (markers evaluated per recipient). `blocks` / `email_settings` are the
  // editor's source of truth, round-tripped verbatim.
  content_mode?: EmailContentMode
  blocks?: Block[] | null
  email_settings?: EmailSettings | null
  compiled_html?: string | null
  template_key?: TemplateKey | null
  // Stamped by the builder at save time: `compiled_html` contains a product
  // section that resolved to no products (it would send silently empty).
  // The server refuses to send/schedule a flagged record. Absent/null on
  // records saved before the flag existed — those stay sendable.
  has_empty_product_blocks?: boolean | null
}

export type EmailContentMode = 'layout' | 'blocks'

// POST /loyalty/campaigns/audience-preview — the builder's selected-audience
// card. `total` is the filtered audience; the eligible counts are what a
// send on each channel would reach.
export interface CampaignAudiencePreview {
  total: number
  email_eligible: number
  sms_eligible: number
}

// Shared by campaigns and flows (spec §1.7): every key is optional because
// older docs predate each rollout — render "—", never 0, for an absent key.
export interface CampaignStats {
  recipients?: number
  sent?: number
  failed?: number
  // Recipients skipped (opt-outs, missing phone/email, cart already converted).
  skipped?: number
  // SMS: real delivery confirmations from the Twilio status callback.
  // EMAIL: SendGrid ACCEPTING the send (a 202) -- not confirmed inbox
  // delivery, which would need the SendGrid Event Webhook / Email Activity
  // API, neither of which is enabled. So for email this tracks `sent`, and
  // the two diverge only when a send errors. Legacy rows sent before this
  // was recorded carry 0 or no value at all.
  delivered?: number
  // Unique recipient opens via the tracking pixel (inflated by Apple MPP).
  opens?: number
  // Unique recipients who followed a tracked link — the dependable one.
  clicks?: number
  // Attribution (campaign_attribution.py): session rule first, then the
  // recipient-email 7-day fallback. Revenue = order total; conversions =
  // distinct orders.
  revenue?: number
  conversions?: number
  attributed_orders?: number
  attribution_at?: string
  attribution_final?: boolean
  // Recipients who unsubscribed through this campaign's / flow's link.
  unsubscribes?: number
}

export interface LoyaltyCampaign extends CampaignEmailContent {
  id: string
  name: string
  subject: string
  // Legacy rows may still carry bonus|winback|tier|reward|referral — display
  // them as 'email' (see campaignDisplay.campaignType). Absent on very old docs.
  type?: CampaignType | LegacyCampaignType
  // Forward-compat only: `_serialize_campaign` does not emit this today (it
  // normalises `type` in place instead). Honoured first by
  // normalizeCampaignType should the server start sending it.
  display_type?: CampaignType
  channel?: CampaignChannel
  sms_body?: string
  status: CampaignStatus
  // Why a launched campaign is held or failed, worded for the merchant;
  // null/absent when there is nothing to say.
  status_note?: string | null
  audience: CampaignAudience
  stats: CampaignStats
  reward?: CampaignReward
  schedule_mode?: CampaignScheduleMode
  scheduled_at?: string
  // Optional end of the promotional window — display only (the calendar
  // spans the chip from the send date to this).
  ends_at?: string
  sent_at?: string
  created_at?: string
}

export interface CreateCampaignInput extends CampaignEmailContent {
  name: string
  type?: CampaignType
  channel?: CampaignChannel
  // Required (non-empty, ≤1600 chars) when channel is 'sms'.
  sms_body?: string
  audience: CampaignAudience
  send: CampaignSendMode
  // The send-time choice the builder made, sent alongside `send` so a
  // `send: 'draft'` save still records whether the campaign is meant to go
  // out now / at a fixed time / on Smart send time. Without it a draft comes
  // back as 'now' and the Smart choice is silently lost on reopen.
  schedule_mode?: CampaignScheduleMode
  scheduled_at?: string
  ends_at?: string | null
  reward?: CampaignReward
}

// PATCH body for campaigns — lifecycle transitions and/or content edits.
// Lifecycle: legal moves (server-checked) are draft→scheduled (future
// scheduled_at required), scheduled→paused, paused→scheduled (future
// scheduled_at), scheduled→draft, paused→draft. Content fields are accepted
// only while the campaign is draft/scheduled/paused; sending/sent/failed
// reject content changes with a 400.
export interface UpdateCampaignInput extends CampaignEmailContent {
  status?: 'draft' | 'scheduled' | 'paused'
  // Editable only while the campaign is still a draft (the builder locks the
  // Schedule step for anything already scheduled/in flight) — it lets a
  // saved draft correct its send-time choice, Smart included.
  schedule_mode?: CampaignScheduleMode
  scheduled_at?: string
  ends_at?: string | null
  name?: string
  type?: CampaignType
  sms_body?: string
  audience?: CampaignAudience
  reward?: CampaignReward
}

// POST /loyalty/campaigns/preview — the builder's live sidebar. Rendered with
// sample tokens (Avery Nguyen, avery@example.com, coupon = the selected code
// or WELCOME10, cart_total $87.50, cart_url → the store's cart). Exactly the
// backend's accepted body — no sender fields.
export interface PreviewCampaignInput {
  subject?: string
  preview_text?: string
  body_html?: string
  cta_text?: string
  cta_url?: string
  logo_url?: string | null
  coupon_code?: string
  type?: CampaignType
  // Block emails: the server renders `compiled_html` instead of the layout.
  content_mode?: EmailContentMode
  compiled_html?: string | null
  blocks?: Block[] | null
  email_settings?: EmailSettings | null
}

export interface PreviewCampaignResult {
  subject: string
  html: string
}

// POST /loyalty/campaigns/test-send — the review step's "Test Before Sending"
// card. Same content-in-the-body shape as /preview (so an UNSAVED draft can be
// tested), plus the destination address and the campaign's sender overrides.
// The server enqueues a worker task and answers immediately; the message is
// rendered with the SAME placeholder tokens as the preview and carries no open
// pixel, no click rewriting and no campaign stats.
export interface TestSendCampaignInput extends PreviewCampaignInput {
  to: string
  from_email?: string
  from_label?: string
}

export interface TestSendCampaignResult {
  queued: boolean
  to: string
  channel: CampaignChannel
}

// GET /loyalty/campaigns/brand-logo — null (or '') when the store has no
// usable logo.
export interface BrandLogoResponse {
  brand_logo_url: string | null
  // Store brand colour (hex); the block editor's default brand colour.
  brand_color?: string | null
  // Primary pantry_site base URL ("https://{host}", no trailing slash);
  // null when the store has no pantry site.
  site_base?: string | null
}

// Normalised brand-logo response (gateway → UI). '' when absent.
export interface LoyaltyBrand {
  brandLogoUrl: string
  brandColor: string
  siteBase: string
}

// ── Campaign history (audit log of a campaign's lifecycle) ───────────────────

// Mirrors LoyaltyCampaignEvent.Event on the backend.
export type CampaignHistoryEventType =
  | 'created'
  | 'status_changed'
  // PATCH content edit; detail = {fields: [edited field names]}.
  | 'content_updated'
  | 'send_started'
  | 'send_completed'
  | 'send_failed'
  // Sending paused / picked up again while launched; detail = {note} on hold.
  | 'send_on_hold'
  | 'send_resumed'

// `serialize_event` (loyalty_campaign_event_qs) emits `detail` as an object,
// never a string: `{}` (created), `{scheduled_at}` or `{send: 'now'}`
// (status_changed), `{fields: string[]}` (content_updated), `{recipients}`
// (send_started), `{sent, failed}` (send_completed / send_failed after a
// blast) or `{reason}` (send_failed pre-flight). from_status / to_status /
// actor come back as '' rather than being omitted.
export interface LoyaltyCampaignHistoryEvent {
  id: string
  event: CampaignHistoryEventType
  from_status?: CampaignStatus | ''
  to_status?: CampaignStatus | ''
  actor?: string
  detail?: Record<string, unknown>
  created_at?: string
}

export interface LoyaltyCampaignHistoryResponse {
  events: LoyaltyCampaignHistoryEvent[]
  next_cursor: string | null
}

// ── Flows ────────────────────────────────────────────────────────────────────

export type FlowStatus = 'draft' | 'live' | 'paused'
export type FlowDelayUnit = 'minutes' | 'hours' | 'days'

// Mirrors the backend's ALLOWED_TRIGGER_METRICS (views/loyalty/flows.py)
// exactly — the serializer rejects anything else. 'Birthday' is not a tracked
// storefront event: a daily scheduler fires it for members whose stored
// birthday matches the day. 'abandoned_cart' is the only trigger the v2
// builder offers; the storefront events 'Added to Cart' / 'Started Checkout'
// both feed it (spec §1.6). The others remain valid for flows created earlier.
export type FlowTriggerMetric =
  | 'abandoned_cart'
  | 'Viewed Product'
  | 'Added to Cart'
  | 'Started Checkout'
  | 'Searched Products'
  | 'Placed Order'
  | 'Ordered Product'
  | 'Fulfilled Order'
  | 'Cancelled Order'
  | 'Refunded Order'
  | 'Opened Email'
  | 'Clicked Email'
  | 'Birthday'

export const FLOW_TRIGGER_METRICS: FlowTriggerMetric[] = [
  'abandoned_cart',
  'Viewed Product',
  'Added to Cart',
  'Started Checkout',
  'Searched Products',
  'Placed Order',
  'Ordered Product',
  'Fulfilled Order',
  'Cancelled Order',
  'Refunded Order',
  'Opened Email',
  'Clicked Email',
  'Birthday',
]

// Human-readable trigger label — the legacy wire values double as display
// labels; the two non-event triggers read as member-facing phrasing.
export const flowTriggerLabel = (metric: string): string => {
  if (metric === 'abandoned_cart') return 'Customer abandons cart'
  if (metric === 'Birthday') return "Member's birthday"
  return metric
}

// Builder defaults for the abandoned-cart trigger (spec §1.2).
export const ABANDONED_CART_DEFAULT_DELAY_HOURS = 4

// Which storefront event starts an abandoned-cart flow: 'cart' = any cart
// with items and no order (checkout never needed); 'checkout' = only a
// started checkout with no order.
export type FlowCartTrigger = 'cart' | 'checkout'

// One reminder email (Email 2..4) of an abandoned-cart flow: Email 1's design
// with its own subject / headline / description, sent `delay_value`
// `delay_unit` after the previous email. `compiled_html` is compiled by the
// builder at save time; headline / description are kept for re-editing.
export interface FlowReminder {
  subject: string
  preview_text?: string
  headline: string
  description: string
  compiled_html: string
  has_empty_product_blocks?: boolean
  delay_value: number
  delay_unit: FlowDelayUnit
}

// The abandoned-cart fields shared by flow reads and writes.
export interface FlowCartFields {
  cart_trigger?: FlowCartTrigger
  reminders?: FlowReminder[]
  // Reminders stop once the customer opens an email / visits the site.
  // Placing an order always stops the flow.
  exit_on_open?: boolean
  exit_on_visit?: boolean
}

// Flow stats share the campaign shape (spec §1.7).
export type FlowStats = CampaignStats

export interface LoyaltyFlow extends CampaignEmailContent, FlowCartFields {
  id: string
  name: string
  trigger_metric: FlowTriggerMetric
  delay_value: number
  delay_unit: FlowDelayUnit
  // abandoned_cart only: carts below this subtotal never trigger. 0 = any.
  min_cart_value?: number
  // Frequency cap: skip a customer this flow emailed in the last N days. 0 = off.
  resend_cooldown_days?: number
  // Legacy flows omit this: they are email flows.
  channel?: CampaignChannel
  sms_body?: string
  status: FlowStatus
  stats?: FlowStats
  reward?: CampaignReward
  created_at?: string
}

export interface CreateFlowInput extends CampaignEmailContent, FlowCartFields {
  name: string
  trigger_metric: FlowTriggerMetric
  delay_value: number
  delay_unit: FlowDelayUnit
  min_cart_value?: number
  resend_cooldown_days?: number
  // 'email' (default) or 'sms' (sms_body required).
  channel?: CampaignChannel
  sms_body?: string
  reward?: CampaignReward
  // 'live' creates the flow already running (the builder's Activate);
  // omitted / 'draft' saves it for later (Save Draft).
  status?: 'draft' | 'live'
}

export interface UpdateFlowInput extends CampaignEmailContent, FlowCartFields {
  name?: string
  trigger_metric?: FlowTriggerMetric
  delay_value?: number
  delay_unit?: FlowDelayUnit
  min_cart_value?: number
  resend_cooldown_days?: number
  sms_body?: string
  reward?: CampaignReward
  status?: FlowStatus
}

// ── Flow history (audit log of a flow's triggers) ────────────────────────────

export type FlowHistoryStatus = 'scheduled' | 'sent' | 'failed' | 'skipped'

export interface LoyaltyFlowHistoryEvent {
  id: string
  customer_id: string
  email: string
  metric: string
  status: FlowHistoryStatus
  reason: string
  // Abandoned cart, sent / failed rows: 1 = Email 1, 2+ = reminders.
  email_number?: number | null
  created_at?: string
}

export interface LoyaltyFlowHistoryParams {
  limit?: number
  before?: string
}

export interface LoyaltyFlowHistoryResponse {
  events: LoyaltyFlowHistoryEvent[]
  next_cursor: string | null
}

// ── Flow customers (campaign popup › Activity › Customer view) ───────────────

export interface LoyaltyFlowCustomerIdentity {
  customer_id: string
  email: string
}

export interface LoyaltyFlowCustomer extends LoyaltyFlowCustomerIdentity {
  name: string
  subscribed: boolean
  last_status: FlowHistoryStatus
  last_reason: string
  last_at?: string
  sent: number
  skipped: number
  failed: number
  scheduled: number
}

export interface LoyaltyFlowCustomersParams {
  search?: string
  offset?: number
  limit?: number
}

export interface LoyaltyFlowCustomersResponse {
  customers: LoyaltyFlowCustomer[]
  next_offset: number | null
}

export interface LoyaltyFlowCartItem {
  item_name: string
  item_variant: string
  item_qty: string
  item_price: string
  item_image: string
}

export interface LoyaltyFlowCart {
  session_id: string
  // active | converted | expired
  status: string
  items: LoyaltyFlowCartItem[]
  total: string
  updated_at?: string
}

export interface LoyaltyFlowCustomerDetail {
  customer: LoyaltyFlowCustomerIdentity & { name: string; subscribed: boolean }
  events: LoyaltyFlowHistoryEvent[]
  cart: LoyaltyFlowCart | null
}

// ── Store coupon option (feeds `coupon`-type template fields) ────────────────

export interface StoreCouponOption {
  code: string
  label: string
}
